import {
    DropdownOption,
    DropdownSelectProps,
    OnChangeDefaultCallback,
    OnChangeMultiCallback,
    SelectableDropdownOption,
} from "./DropdownSelect";

export const getSelectedOptions = <T>(
    formattedOptions: DropdownOption<T>[],
    mode: DropdownSelectProps["mode"],
    value: T | T[] | undefined
): DropdownOption<T>[] => {
    switch (mode) {
        case "multi": {
            return (((value ?? []) as T[])
                .map((v: T) =>
                    formattedOptions?.find(
                        (opt) =>
                            opt &&
                            opt.type === "item" &&
                            (opt as SelectableDropdownOption<T>).value === v
                    )
                )
                ?.filter((o) => o !== undefined) ?? []) as DropdownOption<T>[];
        }
        case "default": {
            return (
                formattedOptions?.filter(
                    (opt) =>
                        opt &&
                        opt.type === "item" &&
                        (opt as SelectableDropdownOption<T>).value === value
                ) ?? []
            );
        }
    }
    return [];
};

export const getFilteredOptions = <T>(
    formattedOptions: DropdownOption<T>[],
    searchValue: string,
    allowCreateNew: boolean,
    highlightedIndex: number,
    setHighlightedIndex: (index: number) => void,
    mode: DropdownSelectProps["mode"],
    value: T | T[],
    onChange: DropdownSelectProps<T>["onChange"],
    setIsOpen: (isOpen: boolean) => void,
    setSearchValue: (searchValue: string) => void
): DropdownOption<T>[] => {
    let cleanedSearch = searchValue.trim().toLowerCase();
    if (!cleanedSearch) return formattedOptions ?? [];
    const queries: Record<string, boolean> = {};
    const filtered =
        formattedOptions?.filter((option) => {
            if (option.type === "divider") return false;
            if (option.type === "button") return false;
            if (option.type === "section") return false;

            const itemOption = option as SelectableDropdownOption<T>;

            const searchValues = [
                getSearchKey(itemOption)?.trim().toLowerCase(),
                itemOption.value?.toString()?.trim()
                ?.toLowerCase(),
            ];

            const hasMatch = searchValues.some((v) => {
                const isMatch = v?.includes(cleanedSearch);
                if (isMatch && v) queries[v] = true;
                return isMatch;
            });

            if (hasMatch) {
                return true;
            }

            return false;
        }) ?? [];

    // If allowCreateNew is true, add an option to create a new value
    // Only show when there are no exact matches
    if (allowCreateNew && !queries[searchValue]) {
        filtered.unshift({
            value: searchValue as T,
            type: "button",
            key: "create-new",
            label: `Create "${searchValue}"`,
            onClick: () => {
                onCreateNew(
                    {
                        value: searchValue as T,
                        label: searchValue,
                    } as SelectableDropdownOption<T>,
                    mode,
                    value as T | T[],
                    onChange,
                    setIsOpen,
                    setSearchValue
                );
            },
        } as DropdownOption<T>);
    }
    if (highlightedIndex > filtered.length - 1) {
        setHighlightedIndex(filtered.length - 1);
    }
    return filtered;
};

export const scrollIntoViewIfNeeded = (
    element: HTMLElement | null,
    container: HTMLElement | null
) => {
    if (!element || !container) return false;

    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (elementRect.bottom > containerRect.bottom) {
        // Scroll down if element is below viewport
        element.scrollIntoView({ block: "nearest", behavior: "smooth" });
        return true;
    } else if (elementRect.top < containerRect.top) {
        // Scroll up if element is above viewport
        element.scrollIntoView({ block: "nearest", behavior: "smooth" });
        return true;
    }
    return false;
};

export const calculateDropdownPosition = (
    triggerElement: HTMLDivElement | null
) => {
    if (!triggerElement) return {};

    const rect = triggerElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 275; // max-height of dropdown

    // If there's not enough space below and more space above, position above
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        return {
            bottom: window.innerHeight - rect.top + 4,
            left: rect.left,
            width: rect.width,
        };
    }

    // Default position below
    return {
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
    };
};

export const handleSelect = <T>(
    option: SelectableDropdownOption<T>,
    e: React.MouseEvent,
    setIsOpen: (isOpen: boolean) => void,
    mode: DropdownSelectProps["mode"],
    value: T | T[],
    onChange: DropdownSelectProps<T>["onChange"],
    setSearchValue: (searchValue: string) => void
) => {
    if (option.onClick) {
        option.onClick(e);
    }
    if (option.key === "create-new") {
        return;
    }
    if (option.type === "button") {
        // If it's the create button, we don't want to close the dropdown
        setIsOpen(false);
        return;
    }
    switch (mode) {
        case "multi": {
            const valueArray = (value || []) as T[];
            // IF it's already in the array, remove it
            if (valueArray?.includes(option.value)) {
                (onChange as OnChangeMultiCallback<T>)?.(
                    valueArray?.filter((v) => v !== option.value) ?? [],
                    option,
                    "remove"
                );
            } else {
                (onChange as OnChangeMultiCallback<T>)?.(
                    Array.from(new Set([...(valueArray ?? []), option.value])),
                    option,
                    "select"
                );
            }
            setSearchValue("");
            break;
        }
        case "default": {
            (onChange as OnChangeDefaultCallback<T>)?.(
                option.value,
                option,
                "select"
            );
            setSearchValue("");
            setIsOpen(false);
            break;
        }
    }
};

export const onCreateNew = <T>(
    option: SelectableDropdownOption<T>,
    mode: DropdownSelectProps["mode"],
    value: T | T[],
    onChange: DropdownSelectProps<T>["onChange"],
    setIsOpen: (isOpen: boolean) => void,
    setSearchValue: (searchValue: string) => void
) => {
    switch (mode) {
        case "default": {
            (onChange as OnChangeDefaultCallback<T>)?.(
                option.value,
                option,
                "create"
            );
            break;
        }
        case "multi": {
            (onChange as OnChangeMultiCallback<T>)?.(
                Array.from(new Set([...((value ?? []) as T[]), option.value])),
                option,
                "create"
            );
            setIsOpen(true);
            break;
        }
    }
    setSearchValue("");
};

export const onRemove = <T>(
    option: SelectableDropdownOption<T>,
    mode: DropdownSelectProps["mode"],
    value: T | T[],
    onChange: DropdownSelectProps<T>["onChange"]
) => {
    switch (mode) {
        case "default": {
            (onChange as OnChangeDefaultCallback<T>)?.(
                option.value,
                option,
                "remove"
            );
            break;
        }
        case "multi": {
            (onChange as OnChangeMultiCallback<T>)?.(
                (value as T[])?.filter((v) => v !== option.value) ?? [],
                option,
                "remove"
            );
            break;
        }
    }
};


export const getSearchKey = <T,>(option: SelectableDropdownOption<T>) => {
  if (option.searchKey) return option.searchKey;
  if (typeof option.label === "string") return option.label;
  return "";
};

export const isSelectable = <T,>(option?: DropdownOption<T>) => {
  if (!option) return false;
  return (
    (option.type === "item" || option.type === "button") && !option.disabled
  );
};
